import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RangoliCorners } from "@/components/Festive";

const RV_KEY = "pq_recently_viewed";
const INSPIRED_ROW = { key: "inspired", title: "Inspired by your recent history", sub: "Picked from what you've been browsing" };

function DealTile({ product, rowKey, index, tag, testidPrefix }) {
  const navigate = useNavigate();
  const tagText = typeof tag === "function" ? tag(product) : tag;
  return (
    <button
      type="button"
      data-testid={`${testidPrefix}-tile-${rowKey}-${product.id}`}
      onClick={() => navigate(`/shop/product/${product.id}`)}
      className="group w-36 shrink-0 snap-start rounded-2xl bg-white p-3 text-left shadow-[0_10px_28px_-12px_rgba(0,0,0,0.4)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_36px_-12px_rgba(0,0,0,0.5)] sm:w-44"
    >
      <div className="relative mb-2 flex h-24 items-center justify-center overflow-hidden rounded-xl bg-white sm:h-28">
        <img src={product.image} alt={product.name} loading="lazy"
          className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105" />
        {product.discount_pct > 0 && (
          <span className="absolute left-1 top-1 rounded-md bg-red-600 px-1.5 py-0.5 text-[10px] font-extrabold text-white">
            -{product.discount_pct}%
          </span>
        )}
      </div>
      <p className="pq-tile-name line-clamp-2 min-h-[2rem] text-[11px] font-semibold leading-tight text-neutral-800 sm:text-xs">
        {product.name}
      </p>
      <p className="mt-1 flex items-baseline gap-1.5">
        <span className="pq-tile-price font-display text-sm font-bold text-neutral-900">₹{product.price?.toLocaleString("en-IN")}</span>
        {product.compare_at_price > product.price && (
          <span className="text-[10px] text-neutral-500 line-through">₹{product.compare_at_price?.toLocaleString("en-IN")}</span>
        )}
      </p>
      <p className="pq-tile-tag mt-1.5 text-[8px] font-extrabold uppercase tracking-[0.18em] text-orange-600" data-testid={`${testidPrefix}-tag-${rowKey}-${index}`}>
        {tagText}
      </p>
    </button>
  );
}

/**
 * DealRows — shared horizontally-scrolling product rows on gradient panels.
 * Data: /api/shop/fest-rows (trending = most ordered, deals = biggest
 * discounts, essentials) + /api/shop/inspired (browsing-based, account history
 * when signed in, localStorage for guests). Used by the everyday homepage and
 * the STOCK UP FEST festive homepage with different skins.
 */
export default function DealRows({ defs, tag, panelClass, arrowTextClass, testidPrefix, header, sectionId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [inspired, setInspired] = useState([]);

  useEffect(() => {
    api.get("/shop/fest-rows")
      .then(({ data }) => setRows(data))
      .catch(() => setRows({}));
  }, []);

  useEffect(() => {
    if (user === undefined) return;
    const load = async () => {
      try {
        let ids = [];
        if (user) {
          const { data } = await api.get("/shop/recently-viewed");
          ids = (data.products || []).map((p) => p.product_id);
        } else {
          ids = JSON.parse(localStorage.getItem(RV_KEY) || "[]");
        }
        if (!ids.length) { setInspired([]); return; }
        const { data } = await api.get("/shop/inspired", { params: { ids: ids.slice(0, 12).join(",") } });
        setInspired(data.products || []);
      } catch { setInspired([]); }
    };
    load();
  }, [user]);

  if (!rows) return null;
  const rowDefs = inspired.length ? [...defs, INSPIRED_ROW] : defs;
  const rowData = { ...rows, inspired };

  return (
    <section id={sectionId} data-testid={`${testidPrefix}-rows-section`} className="relative py-14 lg:py-16">
      <div className="mx-auto max-w-7xl space-y-8 px-6 lg:px-12">
        {header}
        {rowDefs.map(({ key, title, sub }) => {
          const products = rowData[key] || [];
          if (products.length === 0) return null;
          const ids = products.map((p) => p.id).join(",");
          return (
            <div
              key={key}
              data-testid={`${testidPrefix}-row-${key}`}
              className={`${panelClass} relative overflow-hidden rounded-3xl p-5 sm:p-7`}
            >
              {panelClass.includes("fest-row-panel") && <RangoliCorners />}
              <div className="relative z-10 mb-4 flex items-center justify-between gap-4">
                <div>
                  <h3 className="pq-row-title font-display text-xl font-semibold tracking-tight text-white sm:text-2xl" data-testid={`${testidPrefix}-row-title-${key}`}>
                    {title}
                  </h3>
                  <p className="pq-row-sub mt-0.5 text-xs font-medium text-white/75">{sub}</p>
                </div>
                <button
                  type="button"
                  aria-label={`View all ${title}`}
                  data-testid={`${testidPrefix}-row-arrow-${key}`}
                  onClick={() => navigate(`/shop?ids=${ids}`)}
                  className={`pq-row-arrow group flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/95 shadow-md transition-transform duration-300 hover:scale-110 ${arrowTextClass}`}
                >
                  <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
              </div>
              <div
                className="relative z-10 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 [scrollbar-width:thin] sm:gap-4"
                data-testid={`${testidPrefix}-row-scroll-${key}`}
              >
                {products.map((p, i) => <DealTile key={p.id} product={p} rowKey={key} index={i} tag={tag} testidPrefix={testidPrefix} />)}
                <button
                  type="button"
                  onClick={() => navigate(`/shop?ids=${ids}`)}
                  data-testid={`${testidPrefix}-row-viewall-${key}`}
                  className="pq-row-viewall flex w-24 shrink-0 snap-start flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-white/40 text-white/90 transition-colors hover:border-white/80 hover:text-white"
                >
                  <ChevronRight className="h-5 w-5" />
                  <span className="text-[10px] font-bold uppercase tracking-widest">View all</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
